import type { Funnel as FunnelType } from "schemas/generated/funnel";

import React from "react";

import funnelsEmptyState from "@editor/assets/v2/funnels/funnels-empty-state.png";
import Input from "@editor/components/common/designSystem/Input";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { trpc } from "@editor/utils/trpc";

import Button from "@replo/design-system/components/button/Button";
import EmptyState from "@replo/design-system/components/empty-state/EmptyState";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import PageHeader from "@replo/design-system/components/PageHeader";
import Popover from "@replo/design-system/components/popover/Popover";
import Separator from "@replo/design-system/components/Separator";
import { skipToken } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  FolderSearch,
  Search,
} from "lucide-react";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";

import FunnelCard from "./components/FunnelCard";
import NewFunnelButton from "./components/NewFunnelButton";
import RecommendedFunnelTemplateGrid from "./components/RecommendedFunnelTemplateGrid";

// TODO (BEN O. 2025-06-24): updatedAt should become lastEditedAt that checks for the pages in the funnel latest updatedAt time
type Field = "name" | "updatedAt" | "createdAt";
type Direction = "asc" | "desc";

type OrderState = {
  field: Field;
  direction: Direction;
};

const DEFAULT_ORDER: OrderState = {
  field: "updatedAt",
  direction: "desc",
};

const sortByOptions = [
  { label: "Alphabetical", value: "name" },
  { label: "Last edited", value: "updatedAt" },
  { label: "Created date", value: "createdAt" },
];

const alphabeticalSortOrderOptions = [
  { label: "A to Z", value: "asc" },
  { label: "Z to A", value: "desc" },
];

const chronologicalSortOrderOptions = [
  { label: "Newest First", value: "desc" },
  { label: "Oldest First", value: "asc" },
];

type FunnelSortDropdownProps = {
  order: OrderState;
  onOrderChange: (order: OrderState) => void;
};

const sortFunnels = (order: OrderState, funnels?: FunnelType[]) => {
  if (!funnels) {
    return [];
  }

  return [...funnels].sort((a, b) => {
    const { field, direction } = order;
    let comparison = 0;

    switch (field) {
      case "name":
        comparison = a.name.localeCompare(b.name);
        break;
      case "updatedAt":
        comparison =
          new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
        break;
      case "createdAt":
        comparison =
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        break;
      default:
        return 0;
    }

    return direction === "asc" ? comparison : -comparison;
  });
};

const FunnelSortDropdown: React.FC<FunnelSortDropdownProps> = ({
  order,
  onOrderChange,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const selectableRef = React.useRef<HTMLDivElement>(null);
  const [selectableWidth, setSelectableWidth] = React.useState<number>(0);

  React.useEffect(() => {
    if (selectableRef.current) {
      setSelectableWidth(selectableRef.current.offsetWidth);
    }
  }, []);

  const getValueLabel = () => {
    const field = order.field;
    let label = "Sort by";

    if (field === "name") {
      label = "Alphabetical";
    } else if (field === "updatedAt") {
      label = "Last edited";
    } else if (field === "createdAt") {
      label = "Created date";
    }

    return label;
  };

  const handleSortChange = (field: Field, direction: Direction) => {
    onOrderChange({ field, direction });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        setIsOpen((prev) => !prev);
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <Popover.Root isOpen={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <div
          ref={selectableRef}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label="Sort options"
          onKeyDown={handleKeyDown}
          className="min-w-full"
        >
          <Button
            variant="secondary"
            size="base"
            startEnhancer={
              order.direction === "asc" ? (
                <ArrowUp size={12} />
              ) : (
                <ArrowDown size={12} />
              )
            }
            endEnhancer={<ChevronDown size={12} />}
            UNSAFE_className="min-w-full text-base font-normal bg-sheet"
            titleAlignment="start"
          >
            {getValueLabel()}
          </Button>
        </div>
      </Popover.Trigger>
      <Popover.Content
        align="start"
        side="bottom"
        className="p-2"
        style={{ width: selectableWidth }}
        shouldPreventDefaultOnInteractOutside={false}
        hideCloseButton
      >
        <div className="text-default flex flex-col gap-2 text-sm">
          <div className="flex flex-col gap-1">
            <h3 className="header-h5 px-2">Sort By</h3>
            {sortByOptions.map((option) => (
              <MenuItem
                key={option.value}
                variant="default"
                size="sm"
                selected={order.field === option.value}
                onClick={() =>
                  handleSortChange(option.value as Field, order.direction)
                }
              >
                {option.label}
              </MenuItem>
            ))}
          </div>
          <Separator />
          <div className="flex flex-col gap-1">
            <h3 className="header-h5 px-2">Sort Order</h3>
            {(order.field === "name"
              ? alphabeticalSortOrderOptions
              : chronologicalSortOrderOptions
            ).map((option) => (
              <MenuItem
                key={option.value}
                variant="default"
                size="sm"
                selected={order.direction === option.value}
                onClick={() =>
                  handleSortChange(order.field, option.value as Direction)
                }
              >
                {option.label}
              </MenuItem>
            ))}
          </div>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
};

function Funnel() {
  const isReaplo = isFeatureEnabled("reaplo");
  const projectId = useCurrentProjectId();
  const [order, setOrder] = React.useState<OrderState>(DEFAULT_ORDER);
  const [searchQueryInputValue, searchQuery, setSearchQuery] =
    useDebouncedState("");

  const { data: funnels } = trpc.funnel.list.useQuery(
    projectId ? { projectId, includeFiles: true } : skipToken,
  );

  const { data: funnelTemplates } = trpc.funnelTemplate.find.useQuery(
    projectId ? { projectId } : skipToken,
  );

  const sortedFunnels = React.useMemo(
    () => sortFunnels(order, funnels),
    [funnels, order],
  );

  const filteredFunnels = sortedFunnels.filter((funnel) =>
    funnel.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  if (!isReaplo) {
    window.location.href = `/projects/${projectId}`;
    return null;
  }

  if (funnels?.length === 0) {
    return (
      <div className="flex h-screen w-full flex-col gap-3 overflow-y-auto">
        <PageHeader title="Funnels" />
        <div className="w-full mx-auto flex flex-col gap-3">
          <div className="flex flex-col justify-center items-center">
            <img
              src={funnelsEmptyState}
              alt="Funnels Empty State"
              className="w-[138px] mt-16"
            />
            <div className="flex flex-col gap-2 text-center">
              <h4 className="header-h4">Let's build your first funnel</h4>
              <p className="text-muted">
                Start from scratch or kickstart conversions with a ready-
                <br />
                made template. Pick one, refine it, and start selling faster.
              </p>
            </div>

            <div className="mt-4">
              <NewFunnelButton />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full flex-col gap-3 overflow-y-auto">
      <PageHeader
        title="Funnels"
        actions={<NewFunnelButton key="new-funnel" />}
      />
      <div className="w-full mx-auto flex flex-col gap-3">
        <div className="flex flex-col gap-6">
          {/* TODO (BEN O. 2025-06-24): Recommened funnels should actually be custom recommended funnels */}
          <RecommendedFunnelTemplateGrid
            templates={funnelTemplates?.slice(0, 3) ?? []}
          />
          <div className="flex gap-4 flex-col">
            <h2 className="header-h4 text-default">Keep Building</h2>
            <div className="flex gap-3 w-full">
              <div className="flex-[7] w-full">
                <Input
                  startEnhancer={<Search size={12} />}
                  placeholder="Search"
                  size="base"
                  layoutClassName="grow"
                  value={searchQueryInputValue}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex-1">
                <FunnelSortDropdown order={order} onOrderChange={setOrder} />
              </div>
            </div>
            {filteredFunnels?.length === 0 ? (
              <EmptyState
                title={`No results matching "${searchQueryInputValue}"`}
                description="Start building with Replo — we can't wait to bring your ideas to life!"
                icon={<FolderSearch className="icon-size-default" />}
                UNSAFE_className="py-[10%]"
              >
                <NewFunnelButton />
              </EmptyState>
            ) : (
              <div className="grid grid-cols-3 gap-4 pb-6">
                {filteredFunnels?.map((funnel) => (
                  <FunnelCard key={funnel.id} funnel={funnel} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Funnel;
